import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy
} from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { UtilsService } from '../shared/utils.service';
import { ApiRequestService } from '../shared/api-request.service';
import { AuthService } from '../shared/auth.service';

interface Breadcrumb {
  name: string;
  uri: string;
}

@Component({
  selector: 'app-admin',
  templateUrl: './admin.component.html',
  styleUrls: ['./admin.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AdminComponent implements OnInit, OnDestroy {
  adminRoutesSubscription: Subscription;

  activeRoute: string = '';

  // Precomputed breadcrumb trail. Rebuilt only when navigation completes so the reference stays
  // stable across change-detection cycles. Building it inside the template's *ngFor instead returns
  // a new array every cycle, which makes Angular tear down and recreate the anchors mid-click and
  // swallows the click event.
  breadcrumbs: Breadcrumb[] = [];

  constructor(
    private router: Router,
    private api: ApiRequestService,
    public utils: UtilsService,
    private authService: AuthService
  ) {}

  ngOnInit() {
    // Seed the current route and breadcrumb trail immediately, then keep them in sync on every
    // completed navigation.
    this.activeRoute = this.router.url;
    this.breadcrumbs = this.buildBreadcrumbs();
    this.adminRoutesSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.activeRoute = this.router.url;
        this.breadcrumbs = this.buildBreadcrumbs();
      });
  }

  /**
   * Stable identity for each breadcrumb so *ngFor reuses DOM nodes instead of recreating them.
   */
  trackByUri(_index: number, crumb: Breadcrumb): string {
    return crumb.uri;
  }

  /**
   * Resolve a URL segment to a human-friendly breadcrumb label. Known admin segments are mapped
   * explicitly (including localised and acronym labels); anything else falls back to a title-cased
   * version of the segment with dashes turned into spaces.
   */
  private getBreadcrumbsName(name: string) {
    switch (name.toLowerCase()) {
      case 'admin':
        return 'Admin';
      case 'accounts':
        return 'Accounts';
      case 'users':
        return 'Users';
      case 'sites':
        return this.utils.getLangTerm(
          'parent-child-sites-combined.plural',
          'Sites'
        );
      case 'codes':
        return 'QR Codes';
      case 'trades':
        return 'Job Titles';
      case 'industries':
        return 'Industries';
      case 'ppe':
        return 'PPE';
      case 'work-permits':
        return 'Work Permits';
      case 'task-analyses-revised':
        return this.utils.getLangTerm(
          'ta-jsa-swms.list.account-title',
          'TA/JSA/SWMS Templates'
        );
      case 'system-notifications':
        return 'System Notifications';
      case 'configuration':
        return 'Configuration';
      case 'documents':
        return 'Documents';
      case 'importer':
        return 'Importer';
      case 'edit':
        return 'Edit';
      case 'view':
        return 'View';
      case 'details':
        return 'Details';
      default:
        // Fallback: turn "some-segment" into "Some Segment".
        return name
          .split('-')
          .filter((word) => word.length > 0)
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
    }
  }

  /**
   * Navigate to a breadcrumb target through the Router explicitly. Using a click handler plus
   * router.navigateByUrl (instead of relying on an anchor's href) guarantees a client-side SPA
   * navigation and never a native full-page browser load.
   */
  navigateTo(uri: string): void {
    this.router.navigateByUrl(uri);
  }

  private buildBreadcrumbs(): Breadcrumb[] {
    const parts = this.activeRoute.split('/').filter((part) => part != '');
    let reconstructedMenu = [];
    let reconstructedRoute: string = '';
    parts.forEach(
      (part: string): void => {
        reconstructedRoute += '/' + part;
        if (!Number(part) && part != 'new') {
          reconstructedMenu.push({
            name: this.getBreadcrumbsName(part),
            uri: reconstructedRoute
          });
        }
      }
    );
    return reconstructedMenu;
  }

  ngOnDestroy() {
    this.adminRoutesSubscription.unsubscribe();
  }

  /**
   * Manually invoke the artisan command to sign all onsite users out.
   */
  runAutoSiteSignOut() {
    // Get confirmation.
    this.utils.showModal(
      'Sign Onsite Users Out',
      'Are you sure you want to sign all onsite users out?',
      () => {
        this.api
          .makeRequest('get', 'v2/admin/auto-site-sign-out', {})
          .then((response) => {
            this.utils.showToast(
              'The command for Automatic ' +
                this.utils.getLangTerm(
                  'parent-child-sites-combined.singular',
                  'Site'
                ) +
                ' Sign Out has been run.'
            );
          });
      }
    );
  }

  /**
   * Send a request to the API to reset the database.
   *
   * @param withSeeds When true, run the full Feature_Seeder suite so every
   *                  feature area is populated with realistic generic data.
   *                  When false, only reference data (trades, industries,
   *                  system notifications) and tester identities are
   *                  seeded, matching the pre-comprehensive-seeders
   *                  behaviour.
   */
  onResetDatabase(withSeeds: boolean) {
    // Check if the environment is production.
    if (this.utils.isProduction()) {
      this.utils.showModal(
        'Reset Database',
        'You cannot reset the production database.'
      );
      return;
    }

    // Confirmation copy is tailored to the chosen mode so the user knows
    // exactly what the reset will (and will not) populate.
    const title = withSeeds
      ? 'Reset with Data Seeding'
      : 'Reset without Data Seeding';
    const scopeBullet = withSeeds
      ? '<li>Every feature area will be populated with realistic test data (sites, employees, contractors, hazards, incidents, documents, forms, toolbox talks, actions, messages, and more).</li>'
      : '<li>Only reference data (trades, industries, system notifications) and tester accounts / users will be seeded. No feature data will be populated.</li>';
    const durationBullet = withSeeds
      ? '<li>It will take several minutes to fully complete.</li>'
      : '<li>It will take under a minute to fully complete.</li>';

    this.utils.showModal(
      title,
      '<p>Are you sure you want to reset the database?</p> <ul><li>This cannot be undone.</li> <li>You will be logged out and notified when it is done.</li> <li>Keep your browser window open until it is done.</li>' +
        scopeBullet +
        durationBullet +
        '<li>You can watch the progress in the Slack application logs. If it gets stuck, ask a developer to reset it manually.</li></ul>',
      () => {
        // Make the request to reset the database. Pass `with_seeds` so the
        // API can pick between ComprehensiveDatabaseSeeder and the base
        // DatabaseSeeder.
        this.api
          .makeRequest('post', 'v2/admin/reset-database', {
            with_seeds: withSeeds
          })
          .then((response) => {
            this.utils.showModal('Database Reset Success', response.message);
          })
          .catch((errorResponse) => {
            this.utils.showModal(
              'Database Reset Failed',
              errorResponse.error.message
            );
          });

        // Sign the user out.
        this.authService.signOut(
          'You will be notified when the database reset is complete.'
        );
      }
    );
  }
}
